import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { IMAGE_FILE_EXTENSIONS } from '../../constants/file-extensions';
import type { IFile } from '../file-upload-lists';
import { FileUploadList } from '../file-upload-lists';
import type { IFileUploader } from '../file-uploader/file-uploader';
import FileUploader from '../file-uploader/file-uploader';
import InlineTextButton from '../inline-text-button/inline-text-button';

export interface FileUploaderAndListProps {
  fileUploaderProps: IFileUploader;
  files: IFile[];
  onRemove: (index: string) => void;
  enableImagePreview?: boolean;
  imagePreviewWidth?: string;
  imagePreviewHeight?: string;
}

const FileUploaderAndList = ({
  fileUploaderProps,
  files,
  onRemove,
  enableImagePreview = false,
  imagePreviewWidth = '100%',
  imagePreviewHeight = '172px',
}: FileUploaderAndListProps) => {
  const { t } = useTranslation();

  const isSingleFileUpload = fileUploaderProps?.maxFilesNumber === 1;
  const previewImageFile = files?.length === 1 ? files[0] : undefined;

  const isImageFile = !!previewImageFile && getIsImageFile(previewImageFile);

  const showImagePreview = enableImagePreview && isImageFile;

  if (showImagePreview && isSingleFileUpload) {
    return (
      <ImagePreviewWrapper>
        <ImagePreview
          data-testid="image-preview"
          src={previewImageFile.file}
          alt={previewImageFile.originalName}
          $imagePreviewWidth={imagePreviewWidth}
          $imagePreviewHeight={imagePreviewHeight}
        />
        <InlineTextButton
          label={t('documentUpload.deleteImage', {
            defaultValue: 'Delete image',
          })}
          // "0" is the index of the first file
          onClick={() => onRemove('0')}
          isUnderlined
        />
      </ImagePreviewWrapper>
    );
  }

  return (
    <Wrapper>
      {files.length > 0 && (
        <ListContainer>
          <FileUploadList files={files} onRemove={onRemove} />
        </ListContainer>
      )}
      <FileUploader {...fileUploaderProps} />
    </Wrapper>
  );
};

export default memo(FileUploaderAndList);

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;

const ImagePreviewWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    align-items: flex-start;
  `}
`;

const ImagePreview = styled.img`
  ${({
    $imagePreviewWidth,
    $imagePreviewHeight,
  }: {
    $imagePreviewWidth: string;
    $imagePreviewHeight: string;
  }) => css`
    width: ${$imagePreviewWidth};
    height: ${$imagePreviewHeight};
    object-fit: cover;
  `}
`;

const ListContainer = styled.ul`
  padding-left: 0;
  margin: 0;
`;

export const getIsImageFile = (file: Partial<IFile>) => {
  if (!file?.file) return false;
  const isDataUrl = file.file.startsWith('data:');

  if (isDataUrl) {
    return file.file.startsWith('data:image');
  }
  return IMAGE_FILE_EXTENSIONS.some((substring) =>
    file.file?.includes(substring)
  );
};
